import { Component, HostListener, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router, RouterOutlet, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, filter, firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { BreakGlassService } from './core/auth/break-glass.service';
import { FatipOidcAuthService } from './core/auth/fatip-oidc.service';
// 0.62.0.1: UserSwitcherComponent entfernt (Persona-Switcher-UI raus).
// 0.62.0.2: FakeAuthService-Import + Inject + showPersonaSwitcher-Computed
// entfernt — OIDC ist primary. 0.62.0.3 entfernt die BE-Build-Tags +
// Dockerfile-Flags.
import { TaskInboxComponent } from './core/tasks/task-inbox.component';
import { FapIconComponent } from './core/ui/fap-icon.component';
import { VoucherCreateModalComponent } from './modules/voucher/voucher-create-modal.component';
// 0.62.1.6 — User-konfigurierbares Logo + Footer-Links via BrandingService.
// 0.62.1.33 — + Flag-Logo + Legal-Link-Overrides + Footer-Title + Env-Label.
import { BrandingService, FooterLink, LegalLinkKey, EnvironmentLabel } from './core/branding/branding.service';
// 0.62.1.17 — User-Profilbild im Header-Avatar.
import { UserPreferencesService } from './core/preferences/user-preferences.service';
// Cut U23 (Issue #398): Persistenz der aktiven Sprache des Sprachmenues.
import { LocalePreferenceService } from './core/preferences/locale-preference.service';
// BUG-008 #13 — Spotlight-Header-Button (platform-konfigurierbar statt hardcoded "Warum? Jetzt!").
import { SpotlightService } from './core/branding/spotlight.service';
import { assetUrl, RELEASES_JSON } from './core/branding/asset-url.util';

@Component({
  selector: 'fap-root',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterOutlet, RouterLink, TranslateModule, TaskInboxComponent, FapIconComponent, VoucherCreateModalComponent],
  templateUrl: './app.component.html',
  styles: [`
    .fap-shell { display: flex; flex-direction: column; min-height: 100vh; }
    .fap-shell--dev { background: linear-gradient(180deg, var(--ob-info-soft) 0, var(--ob-surface) 280px); }
    .fap-shell--dev .fap-header { border-bottom-color: var(--ob-primary); border-bottom-width: 3px; }
    .fap-header__pill { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.05em; background: var(--ob-primary); color: white; padding: 3px 8px; border-radius: 3px; margin-left: 8px; vertical-align: middle; text-transform: uppercase; }
    .fap-header__link { color: var(--ob-primary); text-decoration: none; font-size: 0.9rem; font-weight: 600; }
    /* 0.57.36: Header-Link mit Oblique-Icon-Präfix (Mein Konto, Dev-Portal-Switch). */
    .fap-header__link--with-icon { display: inline-flex; align-items: center; gap: 6px; }

    /* 0.62.0 / Pfad C.1 — Service-Navigation nach Oblique-Pattern.
       Right-aligned items mit dezenten vertical separators.
       (https://oblique.bit.admin.ch/guidelines/service-navigation) */
    .fap-service-nav {
      display: flex;
      align-items: stretch;
      gap: 0;
    }
    .fap-service-nav > * {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 12px;
      color: var(--ob-text, #1c2834);
      text-decoration: none;
      font-size: 0.9rem;
      font-weight: 500;
      border-left: 1px solid var(--ob-border-color, #acb4bd);
      transition: color 120ms, background 120ms;
    }
    .fap-service-nav > *:first-child {
      border-left: none;
    }
    .fap-service-nav__item:hover,
    .fap-service-nav__item:focus-visible {
      color: var(--ob-primary, #d8232a);
      background: var(--ob-surface-alt, #f0f4f7);
    }
    .fap-service-nav__item:focus-visible {
      outline: 2px solid var(--ob-primary, #d8232a);
      outline-offset: -2px;
    }
    /* CTA-Variante für "Anmelden" — etwas prominenter als die anderen
       Service-Nav-Items, aber nicht so laut wie der "Warum? Jetzt!"-Button. */
    .fap-service-nav__item--cta {
      color: var(--ob-primary, #d8232a);
      font-weight: 600;
    }
    .fap-service-nav__item--cta:hover,
    .fap-service-nav__item--cta:focus-visible {
      background: var(--ob-primary-soft, #ffedee);
    }
    /* Icon-only-Variante (Help-Icon im Header) — kompakt, kein Label. */
    .fap-service-nav__item--icon-only .fap-service-nav__label { display: none; }
    /* 0.61.16.2 — Voucher-Button im Header. Eigene Filled-Variante mit
       Brand-Farbe (Primary) statt eines neutralen Buttons mit dünnem
       Border (sah bisher nicht-zur-Headerleiste-zugehörig aus). Pattern:
       leichte Brand-Tinte als Hintergrund, Brand-Border, Hover etwas
       kräftiger. Ähnlich zur 'Warum? Jetzt!'-CTA aber dezenter. */
    .fap-service-nav__item--voucher {
      background: var(--ob-primary-soft, #ffedee);
      border-radius: 4px;
      margin: 4px 6px;
      padding: 4px 12px;
      border: 1px solid var(--ob-primary, #d8232a);
      color: var(--ob-primary, #d8232a);
      font-weight: 600;
    }
    .fap-service-nav__item--voucher:hover,
    .fap-service-nav__item--voucher:focus-visible {
      background: var(--ob-primary, #d8232a);
      color: var(--ob-surface);
    }

    /* 0.62.x — User-Menu (Avatar + Logout-Dropdown) wenn OIDC-eingeloggt.
       Avatar ist ein farbiger Kreis mit User-Initialen aus Email-Local-
       Part. Klick auf Avatar öffnet ein Popover mit Email + Abmelden. */
    .fap-header__user { position: relative; padding: 0 !important; border-left: 1px solid var(--ob-border-color, #acb4bd); }
    .user-trigger {
      display: inline-flex; align-items: center; gap: 6px;
      background: transparent; border: 0;
      padding: 4px 12px; cursor: pointer;
      color: var(--ob-text); white-space: nowrap;
      transition: color 120ms, background-color 120ms;
      height: 100%;
    }
    .user-trigger:hover { background: var(--ob-surface-alt, #f0f4f7); }
    .user-trigger.open { background: var(--ob-primary-soft, #ffedee); }
    .user-avatar {
      display: inline-flex; align-items: center; justify-content: center;
      width: 32px; height: 32px; border-radius: 50%;
      background: linear-gradient(135deg, #1a4d8a 0%, #2b6cb0 100%);
      color: var(--ob-surface); font-weight: 700; font-size: 0.78rem;
      letter-spacing: 0.02em;
      object-fit: cover;
      flex-shrink: 0;
    }
    /* 0.62.1.17 — Avatar als <img> wenn Profilbild gesetzt. */
    .user-avatar--image {
      background: transparent;
      border: 1px solid var(--ob-border-color, #acb4bd);
    }
    .user-menu__edit {
      padding: 8px 14px;
      background: var(--ob-surface-subtle);
      border-top: 1px solid var(--ob-surface-neutral);
      border-bottom: 1px solid var(--ob-surface-neutral);
      display: flex; flex-direction: column; gap: 8px;
    }
    .user-menu__edit-label { display: flex; flex-direction: column; gap: 4px; font-size: 0.78rem; color: var(--ob-text-soft); }
    .user-menu__edit-label input { padding: 5px 8px; border: 1px solid var(--ob-border-light); border-radius: 3px; font-size: 0.85rem; }
    .user-menu__edit-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
    .user-menu__edit-file { font-size: 0.78rem; color: var(--ob-primary); cursor: pointer; padding: 4px 0; }
    .user-menu__edit-file input[type="file"] { display: block; font-size: 0.75rem; margin-top: 2px; }
    .user-menu__edit-error { font-size: 0.78rem; color: var(--ob-error, #99191e); margin: 0; padding: 4px 6px; background: var(--ob-error-soft, #ffedee); border-radius: 3px; }
    .user-menu__edit-hint { font-size: 0.72rem; margin: 0; }
    .user-trigger__caret { font-size: 0.7rem; opacity: 0.7; }
    .user-menu {
      position: absolute; right: 0; top: calc(100% + 4px);
      list-style: none; padding: 4px 0; margin: 0;
      background: var(--ob-surface); border: 1px solid var(--ob-border-color, #acb4bd);
      border-radius: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.12);
      min-width: 220px; z-index: 100;
    }
    .user-menu__email {
      padding: 10px 14px 6px; font-size: 0.82rem;
      color: var(--ob-text-muted, #596978); word-break: break-all;
    }
    .user-menu__sep { border: 0; border-top: 1px solid var(--ob-border-color, #acb4bd); margin: 0; }
    .user-menu__item {
      display: inline-flex; align-items: flex-start; gap: 10px;
      width: 100%; text-align: left;
      background: transparent; border: 0;
      padding: 10px 14px; cursor: pointer;
      font-size: 0.92rem; color: var(--ob-text);
    }
    .user-menu__item:hover { background: var(--ob-surface-alt, #f0f4f7); color: var(--ob-primary, #d8232a); }
    .user-menu__item-stack { display: flex; flex-direction: column; gap: 2px; }
    .user-menu__item-stack strong { font-size: 0.92rem; font-weight: 600; }
    .user-menu__item-stack small { font-size: 0.78rem; color: var(--ob-text-muted, #596978); line-height: 1.35; }
    .user-menu__item:hover .user-menu__item-stack small { color: var(--ob-primary, #d8232a); opacity: 0.8; }
    /* Strong-Variante (Logout-from-IdP, prominenter) — leicht roter
       Akzent damit der "echte" Logout sichtbar prominent ist. */
    .user-menu__item--strong { border-top: 1px solid var(--ob-border-color, #acb4bd); }
    .user-menu__item--strong .user-menu__item-stack strong { color: var(--ob-primary, #d8232a); }
    .fap-service-nav__label {
      white-space: nowrap;
    }
    /* Mobile/narrow: nur Icons, Labels ausblenden ab <720px */
    @media (max-width: 720px) {
      .fap-service-nav__label { display: none; }
      .fap-service-nav > * { padding: 4px 10px; }
    }
    .fap-header {
      background: var(--ob-surface, #ffffff);
      border-bottom: 1px solid var(--ob-border-color, #acb4bd);
      padding: 12px 24px;
      display: flex;
      align-items: center;
      gap: 24px;
    }
    .fap-header__logo { display: flex; align-items: center; gap: 12px; }
    .fap-header__logo img { height: 44px; width: auto; }
    /* 0.62.1.33 — Environment-Label-Overlay ueber dem Wappen.
       0.62.1.34 — Corner-Ribbon-Pattern (PO-Befund 2026-05-22: vorige
       Variante mit width 64px + rotate -30deg war zu schmal, schlecht
       lesbar und falsch positioniert). Jetzt: overflow:hidden kappt
       die Ribbon-Enden an den Wappen-Raendern; rotate(-45deg) ohne
       transform-origin (Default = center); breiter + fetter + groesseres
       letter-spacing fuer Lesbarkeit. */
    .fap-header__flag-wrap {
      position: relative;
      display: inline-block;
      height: 44px;
      line-height: 0;
      overflow: hidden;
    }
    .fap-header__flag-wrap img { display: block; height: 44px; width: auto; }
    .fap-header__flag-wrap:not([data-env-label])::after { display: none; }
    .fap-header__flag-wrap[data-env-label]::after {
      content: attr(data-env-label);
      position: absolute;
      top: 8px;
      left: -24px;
      width: 90px;
      padding: 2px 0;
      font-size: 0.68rem;
      font-weight: 800;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      letter-spacing: 0.1em;
      text-align: center;
      color: #1a1a1a;
      background: #ffd400;
      transform: rotate(-45deg);
      box-shadow: 0 1px 2px rgba(0,0,0,0.35);
      pointer-events: none;
      z-index: 2;
    }
    .fap-header__flag-wrap--DEV[data-env-label]::after { background: #ffd400; color: #1a1a1a; }
    .fap-header__flag-wrap--TEST[data-env-label]::after { background: #f97316; color: var(--ob-surface); }
    .fap-header__flag-wrap--UAT[data-env-label]::after { background: #2b6cb0; color: var(--ob-surface); }
    .fap-header__app {
      font-size: 1.35rem;
      font-weight: 600;
      margin-left: 8px;
      display: inline-flex;
      align-items: center;
      gap: 10px;
      flex-wrap: wrap;
      /* 0.57.64 W-13: alle Header-Items dürfen nicht innerhalb des Wortes
         umbrechen — vorher kam "FATI P", "v0.57.6 2", "Warum? Jetzt!" mit
         Mid-Word-Wrap. nowrap pro Item; flex-wrap erlaubt Wrap zwischen
         Items wenn der Header schmal ist. */
    }
    .fap-header__app > * { white-space: nowrap; }
    /* 0.56.9 — Version-Chip neben dem FATIP-Brand. Liest sich aus
     * releases.json[0] (= jüngster Eintrag) und ist damit
     * automatisch synchron mit der ADR-013-Doku-Disziplin.
     * 0.56.10 — als RouterLink auf /modules/roadmap, damit ein Klick
     * direkt zur Roadmap-Übersicht (Changelog | Mittelfristig |
     * Langfristig) führt. */
    .fap-header__version {
      font-size: 0.7rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      background: var(--ob-surface-alt);
      color: var(--ob-text-muted);
      border: 1px solid var(--ob-border-color);
      padding: 2px 8px;
      border-radius: 10px;
      font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
      text-decoration: none;
      transition: border-color 120ms, color 120ms, background 120ms;
    }
    a.fap-header__version:hover {
      border-color: var(--ob-primary);
      color: var(--ob-primary);
      background: var(--ob-primary-soft);
    }
    a.fap-header__version:focus-visible {
      outline: 2px solid var(--ob-primary);
      outline-offset: 2px;
    }
    /* 0.57.60 ADR-040 — "Warum? Jetzt!"-Button. Auffälliger als die
       Versions-Chip, damit Stakeholder beim Walkthrough sehen "ja,
       hier kann ich nachlesen warum es das braucht".
       0.61.22.2 — Farbe an --ob-primary gebunden (CSS-Variable), damit
       der Style-Branding-Tab-Preset auch den Header-Button ändert. */
    .fap-header__why {
      font-size: 0.78rem;
      font-weight: 700;
      letter-spacing: 0.02em;
      background: var(--ob-primary, #d8232a);
      color: var(--ob-surface);
      padding: 4px 12px;
      border-radius: 12px;
      text-decoration: none;
      transition: transform 100ms, box-shadow 100ms, background 200ms;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    }
    .fap-header__why:hover {
      transform: translateY(-1px);
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.3);
      filter: brightness(1.1);
    }
    .fap-header__why:focus-visible {
      outline: 2px solid var(--ob-surface);
      outline-offset: 2px;
    }
    .fap-header__spacer { flex: 1 1 auto; }

    /* 0.57.65 W-16 — Kompakter Sprach-Dropdown.
       0.62.0 / Pfad C.1 — Trigger ist jetzt Teil der Service-Navigation,
       kein eigener Button mit Border mehr. Border kommt vom Service-Nav-
       Wrapper (border-left als Separator). */
    .fap-header__lang { position: relative; padding: 0 !important; border-left: 1px solid var(--ob-border-color, #acb4bd); }
    .lang-trigger {
      display: inline-flex; align-items: center; gap: 4px;
      background: transparent; border: 0;
      padding: 4px 12px; cursor: pointer; font-weight: 500; font-size: 0.9rem;
      color: var(--ob-text); white-space: nowrap;
      transition: color 120ms, background-color 120ms;
      height: 100%;
    }
    .lang-trigger:hover { color: var(--ob-primary); background: var(--ob-surface-alt, #f0f4f7); }
    .lang-trigger.open { color: var(--ob-primary); background: var(--ob-primary-soft, #ffedee); }
    .lang-trigger__caret { font-size: 0.7rem; opacity: 0.7; }
    .lang-menu {
      position: absolute; right: 0; top: calc(100% + 4px);
      list-style: none; padding: 4px 0; margin: 0;
      background: var(--ob-surface); border: 1px solid var(--ob-border-color, #acb4bd);
      border-radius: 3px; box-shadow: 0 4px 12px rgba(0,0,0,0.12);
      min-width: 80px; z-index: 100;
    }
    .lang-menu li { padding: 0; }
    .lang-menu button {
      width: 100%; text-align: left; background: transparent; border: 0;
      padding: 8px 14px; cursor: pointer; font-weight: 500; font-size: 0.92rem;
      color: var(--ob-text);
    }
    .lang-menu button:hover { background: var(--ob-surface-alt, #f0f4f7); }
    .lang-menu button.active { color: var(--ob-primary); font-weight: 700; background: var(--ob-primary-soft, #ffedee); }
    .fap-main { flex: 1 1 auto; }
    .fap-footer {
      border-top: 1px solid var(--ob-border-color, #acb4bd);
      padding: 16px 24px;
      font-size: 0.85rem;
      color: var(--ob-text-muted, #596978);
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
    }
    .fap-footer a { color: inherit; text-decoration: underline; }
  `]
})
export class AppComponent implements OnInit, OnDestroy {
  private translate = inject(TranslateService);
  private router = inject(Router);
  private http = inject(HttpClient);
  // 0.62.0.2: FakeAuthService-Inject raus (Persona-Switcher-Removal Step 2/3)
  bg = inject(BreakGlassService);
  oidc = inject(FatipOidcAuthService);

  // 0.62.x — Header zeigt eine auth-aware Service-Navigation (Oblique-
  // Pattern). Vier Auth-Zustände mit unterschiedlicher Header-Variante:
  //
  //   1. oidcAuthenticated()  — echte FED-Login-vNEXT-Session (IdP-OIDC).
  //                             User-Avatar + Logout-Dropdown statt Login-CTA.
  //   2. bg.isActive()        — Break-Glass/Bootstrap-Session (Notfall-Pfad).
  //                             Same Header-Controls wie OIDC + Persona-Switcher
  //                             im Dev-Build.
  //   3. anonymous + dev      — kein echtes Auth, aber Dev-Build. Vorher
  //                             zeigte der Header Inbox/My-Account/Persona;
  //                             ist semantisch falsch (nichts ist authentisch).
  //                             Jetzt: behandelt wie 4 (anonymous), aber
  //                             Persona-Switch erscheint NACH BG/OIDC-Login.
  //   4. anonymous            — kein Auth (Pilot/Stakeholder-View). Header
  //                             zeigt nur Help-Icon + Login-CTA + Sprache.
  //
  // hasRealSession() = echte Auth (OIDC oder BG). Inbox + My-Account.
  // showLoginCta()    = Login-Button sichtbar (alles außer OIDC + BG).
  // 0.62.0.2: showPersonaSwitcher entfernt (Persona-Switcher-Removal
  // Step 2/3). Templates referenzieren das Computed nicht mehr seit
  // 0.62.0.1.
  oidcAuthenticated = computed(() => this.oidc.authenticated());
  hasRealSession = computed(() => this.oidcAuthenticated() || this.bg.isActive());
  isAnonymous = computed(() => !this.hasRealSession());
  showLoginCta = computed(() => this.isAnonymous());

  // Backward-Compat-Alias: hasSession war früher 'irgendeine Session inkl
  // Dev-Auto-Login'. Jetzt strikt = hasRealSession. Templates die das nutzen
  // werden mitgewandelt (Inbox + My-Account sichtbar nur post-real-login).
  hasSession = computed(() => this.hasRealSession());

  // Display-Werte für den User-Avatar.
  // 0.61.15 — Fallback auf Break-Glass-Email wenn nicht OIDC-eingeloggt.
  // PO-Befund 2026-05-03: 'owner@fatip.local hat keine Möglichkeit zum
  // ausloggen' — Header-Avatar war nur für oidcAuthenticated() sichtbar,
  // BG-User sah gar kein Logout-UI im Header.
  userEmail = computed(() => {
    const oidcEmail = this.oidc.email();
    if (oidcEmail) return oidcEmail;
    const bgSess = this.bg.session();
    return bgSess?.email ?? '';
  });
  userInitials = computed(() => {
    const email = this.userEmail();
    if (!email) return '';
    const local = email.split('@')[0];
    const parts = local.split(/[._-]/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return local.slice(0, 2).toUpperCase();
  });

  // 0.62.x — User-Menu-Dropdown-State (Avatar-Klick öffnet Logout-Eintrag).
  userMenuOpen = signal<boolean>(false);

  // 0.62.x ADR-088 — Voucher-Modal-State (Header-Button "FATIP Voucher
  // erstellen"). Sichtbar nur für hasRealSession (eingeloggte User).
  voucherModalOpen = signal<boolean>(false);
  openVoucherModal() { this.voucherModalOpen.set(true); }
  closeVoucherModal() { this.voucherModalOpen.set(false); }

  toggleUserMenu(ev: Event) {
    ev.stopPropagation();
    this.userMenuOpen.update(v => !v);
  }

  /**
   * logoutFromApp — nur lokale Tokens cleart, die IdP-Session bleibt
   * aktiv. Bei nächstem Login-Klick passiert silent-SSO (kein
   * Password-Prompt). Standard-OIDC-Pattern für Multi-App-Setups.
   */
  logoutFromApp() {
    this.userMenuOpen.set(false);
    this.oidc.logoutLocalOnly();
  }

  /**
   * logoutFromIdp — full Single-Logout. Cleart lokale Tokens UND
   * beendet die IdP-Server-Session. Beim nächsten Login muss
   * Password neu eingegeben werden.
   */
  logoutFromIdp() {
    this.userMenuOpen.set(false);
    this.oidc.logoutFromIdp();
  }

  /**
   * 0.61.15 — logoutBreakGlass: cleart die Break-Glass-Session und
   * navigiert zur Welcome-Page. PO-Befund 2026-05-03: Bootstrap-Owner
   * (owner@fatip.local) hatte keine Möglichkeit sich auszuloggen weil
   * der Header-Logout nur für OIDC-User sichtbar war + die Logout-
   * Buttons nur logoutFromIdp aufriefen (no-op für BG).
   */
  logoutBreakGlass() {
    this.userMenuOpen.set(false);
    this.bg.clear();
    // Hard-Reload damit alle reactive Subscriptions die BG-Session-
    // Removal mitkriegen + die Welcome-Gate-Navigation sauber greift.
    window.location.href = '/';
  }

  // 0.62.x — Direkter Login-Pfad aus dem Header-CTA. User-Wunsch
  // 2026-05-02: regulärer User soll mit einem Klick zum IdP
  // (FED-Login vNEXT) gelangen, NICHT erst über das /login-Switchboard
  // mit drei Optionen (Bootstrap / Break-Glass / OIDC). Switchboard
  // bleibt unter /login erreichbar als Fallback für Operations-Pfade
  // (man muss die URL aber explizit kennen).
  //
  // Wenn OIDC nicht konfiguriert ist (Auto-Seed nicht durch oder
  // Backend down): Fallback zur Switchboard. Sonst sieht der User nur
  // einen toten Link.
  startLogin(ev: Event) {
    ev.preventDefault();
    if (this.oidc.isConfigured()) {
      this.oidc.loginWithRedirect();
    } else {
      void this.router.navigate(['/login']);
    }
  }

  // Legacy-Alias bis alle Templates auf hasSession umgestellt sind. Nicht
  // mehr benutzen — nutze hasSession() / showLoginCta() / showPersonaSwitcher().
  isLoggedIn = computed(() => this.hasSession());

  languages = ['de', 'fr', 'it', 'en'] as const;
  // Cut U23 (Issue #398): aus der aktiven Sprache initialisiert, nicht aus
  // dem hardcoded 'de'. Die APP_INITIALIZERs laufen vor diesem Constructor:
  // der Locale-Initializer hat den gespeicherten Locale bereits angewendet,
  // und das Sprachmenue soll genau diesen lesen.
  currentLang = signal<string>(this.translate.currentLang || 'de');

  // 0.57.65 W-16 — Sprach-Dropdown-State. Open via Trigger-Klick, Close
  // automatisch beim Outside-Click (HostListener unten) oder ESC.
  langMenuOpen = signal<boolean>(false);

  toggleLangMenu(ev: Event) {
    ev.stopPropagation();
    this.langMenuOpen.update(v => !v);
  }

  @HostListener('document:click')
  onDocClick() {
    if (this.langMenuOpen()) this.langMenuOpen.set(false);
    if (this.userMenuOpen()) this.userMenuOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  onEscape() {
    if (this.langMenuOpen()) this.langMenuOpen.set(false);
    if (this.userMenuOpen()) this.userMenuOpen.set(false);
  }

  // U13 task 5 (S21) — Up/Down bewegt den Fokus durch die Items des
  // geoeffneten Menüs (menuitem im User-Menü, option im Sprachmenü), im
  // selben Keyboard-Handling wie Escape und Outside-Click. Steht der Fokus
  // schon auf einem Item, wird relativ dazu gewechselt (mit Wrap am Ende);
  // sonst springt Down aufs erste und Up aufs letzte. Ohne offenes Menü
  // greift die Behandlung nicht, damit die Pfeiltasten weiter scrollen.
  // Review-Fix task 5: steht der Fokus in einem Text-Eingabefeld, ebenfalls
  // nicht — das Profilbild-URL-Input sitzt im User-Menü und braucht seine
  // Caret-Navigation, sonst reisst Down/Up den Fokus aus dem Feld.
  @HostListener('document:keydown.arrowdown', ['$event'])
  @HostListener('document:keydown.arrowup', ['$event'])
  onMenuArrowKey(ev: KeyboardEvent) {
    const active = document.activeElement;
    if (active instanceof HTMLElement &&
        (active.matches('input, textarea, select') || active.isContentEditable)) {
      return;
    }
    const list = this.arrowTargetList();
    if (!list) return;
    const items = Array.from(
      list.querySelectorAll<HTMLButtonElement>('button[role="menuitem"], button[role="option"]')
    ).filter(b => !b.disabled);
    if (items.length === 0) return;
    ev.preventDefault();
    const index = items.indexOf(document.activeElement as HTMLButtonElement);
    if (index === -1) {
      (ev.key === 'ArrowDown' ? items[0] : items[items.length - 1]).focus();
      return;
    }
    const step = ev.key === 'ArrowDown' ? 1 : -1;
    items[(index + step + items.length) % items.length].focus();
  }

  /** Das List-Element, auf das sich eine Pfeiltaste bezieht: die Liste, die
   *  den Fokus schon enthält, sonst das User-Menü, sonst das Sprachmenü —
   *  jeweils nur wenn es offen (und damit gerendert) ist. */
  private arrowTargetList(): HTMLElement | null {
    const user = document.getElementById('fap-user-menu');
    const lang = document.getElementById('fap-lang-menu');
    const active = document.activeElement;
    if (active instanceof HTMLElement) {
      if (user?.contains(active)) return user;
      if (lang?.contains(active)) return lang;
    }
    if (this.userMenuOpen() && user) return user;
    if (this.langMenuOpen() && lang) return lang;
    return null;
  }

  // 0.56.9: aktuelle Plattform-Version aus releases.json[0]. Per ADR-013
  // ist releases.json die Source-of-Truth für ausgelieferte Releases —
  // ein neuer Release-Eintrag oben in der Liste rotiert automatisch
  // diesen Header-Chip mit, ohne separates Versions-Konstanten-Refactor.
  appVersion = signal<string>('');

  // BUG-008 #13 — Spotlight-Header-Button. Default false (hidden) bis der
  // Fetch resolved — vermeidet ein Flash-of-hardcoded-Content ("Warum?
  // Jetzt!") bevor die Platform-Konfiguration geladen ist. Fetched einmal
  // in ngOnInit für die aktuelle UI-Sprache; SDR-014: spotlightTitle ist
  // admin-kontrolliert und NICHT backend-sanitisiert → im Template NUR via
  // Text-Interpolation rendern, niemals [innerHTML]/bypassSecurityTrustHtml.
  spotlightEnabled = signal<boolean>(false);
  spotlightTitle = signal<string>('');

  // 0.62.x — Branding aus environment.ts. Default „Schweizland · Pilot",
  // Production-Override via fileReplacement oder Runtime-ENV. Keine
  // hardcodeten Bund-Symbole im Template — alles über diese Property.
  readonly branding = environment.branding;

  // 0.62.1.6 — Plattform-Admin kann via Style-Tab Logo-URL/-File und
  // Footer-Links ueberschreiben (localStorage-Pilot). effectiveLogoSrc
  // faellt auf environment.branding.logoName zurueck wenn kein Override
  // gesetzt ist. customFooterLinks sind Append-Only und rendern neben
  // den 4 fixen Legal-Links.
  brandingOverride = inject(BrandingService);
  // 0.62.1.17 — Personliche User-Praeferenzen (Profilbild im Avatar).
  userPrefs = inject(UserPreferencesService);
  // Cut U23 (Issue #398): Persistenz der aktiven Sprache im Sprachmenue
  // (localStorage vor dem Login, GET|PUT /v1/me/locale angemeldet). Das
  // Initializer-Bootstrap liest dieselben Stores (app.config.ts).
  localePrefs = inject(LocalePreferenceService);
  // BUG-008 #13 — Spotlight-Header-Button-Content (Public-Endpoint, kein Auth).
  private spotlightSvc = inject(SpotlightService);
  /** Edit-Mode-Toggle fuer das Profilbild-Setting im User-Menu-Dropdown. */
  profileImageEditOpen = signal<boolean>(false);
  /** Eingabefeld fuer URL (lokaler Draft, vor Save). */
  profileImageUrlInput = signal<string>('');
  /** Fehlertext-Key wenn Upload zu gross / unguelt. */
  profileImageError = signal<string | null>(null);
  effectiveLogoSrc = computed<string>(() => this.brandingOverride.effectiveLogoSrc());
  customFooterLinks = computed<FooterLink[]>(() => this.brandingOverride.footerLinks());

  // 0.62.1.33 — Flag-Logo (Wappen), Footer-Title, Env-Label.
  effectiveFlagSrc = computed<string | null>(() => this.brandingOverride.effectiveFlagSrc());
  effectiveFooterTitle = computed<string>(() => this.brandingOverride.effectiveFooterTitle());
  effectiveEnvLabel = computed<EnvironmentLabel>(() => this.brandingOverride.effectiveEnvLabel());
  showEnvOverlay = computed<boolean>(() => this.brandingOverride.showEnvOverlay());
  // 0.62.1.34 — App-Brand-Name (ersetzt Slogan-Dead-Code).
  effectiveAppName = computed<string>(() => this.brandingOverride.effectiveAppName());

  /** 0.62.1.33 — effektive URL fuer einen Standard-Legal-Link. */
  effectiveLegalLink(key: LegalLinkKey): string {
    return this.brandingOverride.effectiveLegalLink(key);
  }

  /** Footer-Label fuer den aktuellen Sprach-Switch (mit DE-Fallback). */
  footerLabel(link: FooterLink): string {
    const lang = this.currentLang();
    const safe: 'de' | 'en' | 'fr' | 'it' = (lang === 'de' || lang === 'en' || lang === 'fr' || lang === 'it') ? lang : 'de';
    return this.brandingOverride.labelFor(link, safe);
  }

  /** Render-Time-Schema-Check: nur http(s)-URLs werden im Footer angezeigt. */
  isValidHttpUrl(url: string): boolean {
    return this.brandingOverride.isValidHttpUrl(url);
  }

  /** 0.72.65: Render-Time-Schema-Check fuer die vier Standard-Legal-Links:
   *  http(s)-URL ODER absoluter Pfad. Gegenstueck zu isValidHttpUrl, das
   *  fuer die Custom-Footer-Links gilt und den Pfad nicht zulaesst.
   *  Bis 0.72.65 entschied ueber diese vier Links allein die Truthiness,
   *  also renderte auch 'mailto:x' oder 'example.ch/x' einen Footer-Link. */
  isValidLinkTarget(url: string): boolean {
    return this.brandingOverride.isValidLinkTarget(url);
  }

  // 0.62.1.17 — Profilbild-Avatar-Methoden.
  toggleProfileImageEdit(ev: Event): void {
    ev.stopPropagation();
    this.profileImageEditOpen.update(v => !v);
    if (this.profileImageEditOpen()) {
      this.profileImageUrlInput.set(this.userPrefs.profileImage() ?? '');
      this.profileImageError.set(null);
    }
  }

  applyProfileImageUrl(): void {
    const url = this.profileImageUrlInput().trim();
    if (!url) return;
    if (!this.userPrefs.isValidProfileImageSrc(url)) {
      this.profileImageError.set('i18n.fap.user_prefs.profile_image_invalid');
      return;
    }
    const ok = this.userPrefs.setProfileImage(url);
    if (!ok) {
      this.profileImageError.set('i18n.fap.user_prefs.profile_image_storage_quota');
      return;
    }
    this.profileImageError.set(null);
    this.profileImageEditOpen.set(false);
  }

  onProfileImageFileSelected(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (file.size > this.userPrefs.maxProfileImageBytes) {
      this.profileImageError.set('i18n.fap.user_prefs.profile_image_too_large');
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') return;
      const ok = this.userPrefs.setProfileImage(result);
      if (!ok) {
        this.profileImageError.set('i18n.fap.user_prefs.profile_image_storage_quota');
        return;
      }
      this.profileImageUrlInput.set('');
      this.profileImageError.set(null);
      this.profileImageEditOpen.set(false);
    };
    reader.onerror = () => {
      this.profileImageError.set('i18n.fap.user_prefs.profile_image_read_error');
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  resetProfileImage(): void {
    this.userPrefs.reset();
    this.profileImageUrlInput.set('');
    this.profileImageError.set(null);
  }

  // When the browser is on a /developer/* route the layout switches to a
  // developer-portal look (different header text, no admin controls) —
  // same bundle but visually distinct, so external devs know they're in
  // a different "product" without needing a separate subdomain.
  isDeveloperPortal = signal(false);

  private sub: Subscription;

  constructor() {
    this.translate.addLangs([...this.languages]);
    this.translate.setDefaultLang('de');
    // Cut U23 (Issue #398): bewusst KEIN translate.use('de') im Constructor.
    // Die APP_INITIALIZERs laufen vor diesem Constructor: app.config.ts setzt
    // defaultLanguage 'de' und der uiOverlay-Initializer awaitet
    // translate.use(translate.defaultLang), 'de' ist damit geladen, und der
    // Locale-Initializer wendet danach den gespeicherten Locale an. Ein
    // use('de') hier wuerde das reverten: ngx-translate firet changeLang
    // synchron fuer eine bereits geladene Sprache, also noch vor dem ersten
    // Render. locale-initializer.spec.ts pinned das.
    //
    // Keep the flag fresh on every nav so deep links work too.
    this.isDeveloperPortal.set(this.router.url.startsWith('/developer'));
    this.sub = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    ).subscribe(e => this.isDeveloperPortal.set(e.urlAfterRedirects.startsWith('/developer')));
    // Cut U23 (Issue #398): das Sprachmenue folgt der aktiven Sprache bei
    // jedem Wechsel, auch bei einem Wechsel NACH dem Boot: der Server-Leg
    // des Locale-Initializers wechselt die Sprache erst nach bootstrapDone.
    // Die Subscription haengt am selben sub wie der Router-Stream, ngOnDestroy
    // raeumt beide ab.
    this.sub.add(this.translate.onLangChange.subscribe(({ lang }) => this.currentLang.set(lang)));
  }

  ngOnInit() {
    this.http.get<{ releases: { version: string }[] }>(assetUrl(RELEASES_JSON))
      .subscribe({
        next: r => this.appVersion.set(r?.releases?.[0]?.version ?? ''),
        error: () => this.appVersion.set(''),
      });

    // BUG-008 #13 — Fetch once on init (not reactive to language switches;
    // spec calls for a single fetch — a full re-fetch-on-lang-change can be
    // added later if stakeholders need the header button to relabel live).
    void this.loadSpotlight();
  }

  /** GET /v1/content/spotlight once. On error, the button stays hidden
   *  (spotlightEnabled defaults false) — a broken/unreachable backend must
   *  not surface a stale hardcoded label. */
  private async loadSpotlight(): Promise<void> {
    try {
      const dto = await firstValueFrom(this.spotlightSvc.getSpotlight(this.normLang(this.currentLang())));
      this.spotlightEnabled.set(dto.enabled);
      this.spotlightTitle.set(dto.title);
    } catch {
      this.spotlightEnabled.set(false);
    }
  }

  private normLang(l: string | undefined): 'de' | 'en' | 'fr' | 'it' {
    return (l === 'en' || l === 'fr' || l === 'it') ? l : 'de';
  }

  ngOnDestroy() { this.sub?.unsubscribe(); }

  switchLang(lang: string) {
    this.currentLang.set(lang);
    this.translate.use(lang);
    // Cut U23 (Issue #398): schreibt localStorage in JEDEM Zustand und den
    // Server-Write PUT /v1/me/locale nur bei angemeldetem Leser, ohne auf
    // ihn zu warten (Write-both-Regel, Plan U23). Die languages-Liste oben
    // liefert Lowercase-Codes, keine Kanonisierung noetig.
    this.localePrefs.persist(lang, this.oidc.authenticated());
  }
}
